import { useState, useEffect, useCallback, useRef } from 'react';
import {
  FacilityCompliancePayload,
  LicensingRequirements,
  AccreditationRequirements,
  ComplianceComparison,
  AuditChecklistItem,
  PenaltiesAndTotalExposure,
  Citation,
} from '@services/facilityCompliance';
import { getApiOrigin } from '@utils/apiOrigin';

const facilityComplianceApiOrigin = getApiOrigin();

// SSE Event types from the server
export type StreamingEventType = 'init' | 'section' | 'complete' | 'error';

export interface StreamingInitEvent {
  entityDetails: {
    organization: string;
    facility_name: string;
    facility_type: string;
    country: string;
    state: string;
    county: string;
    city: string;
    siteUrl: string;
    documents: string[];
    asOfDate: string;
  };
  generatedAt: string;
}

export interface StreamingSectionEvent {
  section: string;
  data: any;
}

export interface StreamingCompleteEvent {
  status: 'complete';
}

export interface StreamingErrorEvent {
  error: string;
  fields?: string[];
}

// Sections state for progressive rendering
export interface StreamingSections {
  sourceLinks: Citation[] | null;
  licensingRequirements: LicensingRequirements | null;
  accreditationRequirements: AccreditationRequirements | null;
  complianceComparison: ComplianceComparison | null;
  auditReadyChecklist: AuditChecklistItem[] | null;
  penaltiesAndTotalExposure: PenaltiesAndTotalExposure | null;
}

export interface StreamingState {
  isConnected: boolean;
  isComplete: boolean;
  error: string | null;
  entityDetails: StreamingInitEvent['entityDetails'] | null;
  generatedAt: string | null;
  sections: StreamingSections;
  completedSections: string[];
  progress: number; // 0-100
}

const TOTAL_SECTIONS = 6;

export function useStreamingCompliance(payload: FacilityCompliancePayload | null) {
  const [state, setState] = useState<StreamingState>({
    isConnected: false,
    isComplete: false,
    error: null,
    entityDetails: null,
    generatedAt: null,
    sections: {
      sourceLinks: null,
      licensingRequirements: null,
      accreditationRequirements: null,
      complianceComparison: null,
      auditReadyChecklist: null,
      penaltiesAndTotalExposure: null,
    },
    completedSections: [],
    progress: 0,
  });

  const eventSourceRef = useRef<EventSource | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setState(prev => ({ ...prev, isConnected: false }));
  }, []);

  const connect = useCallback(async () => {
    if (!payload) return;

    // Reset state
    setState({
      isConnected: true,
      isComplete: false,
      error: null,
      entityDetails: null,
      generatedAt: null,
      sections: {
        sourceLinks: null,
        licensingRequirements: null,
        accreditationRequirements: null,
        complianceComparison: null,
        auditReadyChecklist: null,
        penaltiesAndTotalExposure: null,
      },
      completedSections: [],
      progress: 0,
    });

    const fullUrl = `${facilityComplianceApiOrigin}/api/v1/facility-compliance/stream`;

    const token = localStorage.getItem('access_token');
    
    abortControllerRef.current = new AbortController();

    try {
      console.log('📡 Starting SSE connection to:', fullUrl);

      // Use fetch with ReadableStream for SSE (POST with body)
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      const processEvents = (text: string) => {
        buffer += text;
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        let currentEvent = '';
        let currentData = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            currentData = line.slice(5).trim();
          } else if (line === '' && currentEvent && currentData) {
            // Empty line marks end of event
            handleEvent(currentEvent, currentData);
            currentEvent = '';
            currentData = '';
          }
        }
      };

      const handleEvent = (eventType: string, data: string) => {
        try {
          const parsed = JSON.parse(data);
          console.log(`📥 SSE Event: ${eventType}`, parsed);

          switch (eventType) {
            case 'init':
              setState(prev => ({
                ...prev,
                entityDetails: parsed.entityDetails,
                generatedAt: parsed.generatedAt,
              }));
              break;

            case 'section':
              const sectionEvent = parsed as StreamingSectionEvent;
              setState(prev => {
                const newSections = { ...prev.sections };
                const newCompleted = [...prev.completedSections];

                // Map section name to state key
                switch (sectionEvent.section) {
                  case 'licensingRequirements':
                    newSections.licensingRequirements = sectionEvent.data;
                    // Also check for sourceLinks in the same response
                    if (sectionEvent.data?.sourceLinks) {
                      newSections.sourceLinks = sectionEvent.data.sourceLinks;
                    }
                    if (!newCompleted.includes('licensingRequirements')) {
                      newCompleted.push('licensingRequirements');
                    }
                    break;
                  case 'accreditationRequirements':
                    newSections.accreditationRequirements = sectionEvent.data;
                    if (!newCompleted.includes('accreditationRequirements')) {
                      newCompleted.push('accreditationRequirements');
                    }
                    break;
                  case 'complianceComparison':
                    newSections.complianceComparison = sectionEvent.data;
                    if (!newCompleted.includes('complianceComparison')) {
                      newCompleted.push('complianceComparison');
                    }
                    break;
                  case 'auditReadyChecklist':
                    newSections.auditReadyChecklist = sectionEvent.data;
                    if (!newCompleted.includes('auditReadyChecklist')) {
                      newCompleted.push('auditReadyChecklist');
                    }
                    break;
                  case 'penaltiesAndTotalExposure':
                    newSections.penaltiesAndTotalExposure = sectionEvent.data;
                    if (!newCompleted.includes('penaltiesAndTotalExposure')) {
                      newCompleted.push('penaltiesAndTotalExposure');
                    }
                    break;
                  case 'sourceLinks':
                    newSections.sourceLinks = sectionEvent.data;
                    if (!newCompleted.includes('sourceLinks')) {
                      newCompleted.push('sourceLinks');
                    }
                    break;
                }

                const progress = Math.round((newCompleted.length / TOTAL_SECTIONS) * 100);

                return {
                  ...prev,
                  sections: newSections,
                  completedSections: newCompleted,
                  progress,
                };
              });
              break;

            case 'complete':
              setState(prev => ({
                ...prev,
                isComplete: true,
                isConnected: false,
                progress: 100,
              }));
              break;

            case 'error':
              const errorEvent = parsed as StreamingErrorEvent;
              setState(prev => ({
                ...prev,
                error: errorEvent.error,
                isConnected: false,
              }));
              break;
          }
        } catch (e) {
          console.error('Error parsing SSE event:', e, data);
        }
      };

      // Read stream
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log('📡 SSE stream complete');
          break;
        }
        processEvents(decoder.decode(value, { stream: true }));
      }

    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('📡 SSE connection aborted');
      } else {
        console.error('❌ SSE error:', error);
        setState(prev => ({
          ...prev,
          error: error.message || 'Connection error',
          isConnected: false,
        }));
      }
    }
  }, [payload]);

  // Auto-disconnect on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    ...state,
    connect,
    disconnect,
    isSectionLoaded: (section: keyof StreamingSections) => state.sections[section] !== null,
  };
}

export default useStreamingCompliance;
